import { User, UserPreferences } from '@/types/user.types'

/**
 * Maps user data from snake_case API response to camelCase frontend format
 */
export function mapUserFromApi(apiUser: any): User {
  if (!apiUser) {
    throw new Error('Cannot map null or undefined user')
  }

  // Helper to normalize temperature units
  const normalizeTemperature = (value: string | undefined): 'celsius' | 'fahrenheit' => {
    if (!value) return 'celsius'
    const lower = value.toLowerCase()
    if (lower === 'f' || lower === 'fahrenheit') return 'fahrenheit'
    return 'celsius' // default to celsius for 'C', 'celsius', or anything else
  }

  // Helper to normalize length/distance units
  const normalizeLength = (value: string | undefined): 'metric' | 'imperial' => {
    if (!value) return 'metric'
    const lower = value.toLowerCase()
    if (lower === 'imperial' || lower === 'ft' || lower === 'mi') return 'imperial'
    return 'metric' // default to metric for 'mm', 'm', 'km', 'metric', or anything else
  }

  // Helper to normalize weight units
  const normalizeWeight = (value: string | undefined): 'metric' | 'imperial' => {
    if (!value) return 'metric'
    const lower = value.toLowerCase()
    if (lower === 'imperial' || lower === 'lb' || lower === 'lbs') return 'imperial'
    return 'metric' // default to metric for 'kg', 'g', 'metric', or anything else
  }

  // Helper to normalize dashboard columns
  const normalizeDashboardColumns = (value: string | undefined): '1' | '2' | '3' | '4' => {
    if (value === '1' || value === '2' || value === '3' || value === '4') return value as '1' | '2' | '3' | '4'
    return '2' // default to 2 columns
  }

  // Map preferences if they exist
  const preferences: UserPreferences = apiUser.preferences ? {
    unitsLength: normalizeLength(apiUser.preferences.units_length),
    unitsWeight: normalizeWeight(apiUser.preferences.units_weight),
    unitsTemperature: normalizeTemperature(apiUser.preferences.units_temperature),
    unitsSpeed: (apiUser.preferences.units_speed === 'mph' ? 'mph' : 'kph'),
    dashboardColumns: normalizeDashboardColumns(apiUser.preferences.dashboard_columns),
    showWeatherWidget: apiUser.preferences.show_weather_widget ?? false,
    weatherAccountId: apiUser.preferences.weather_account_id,
    weatherAccountName: apiUser.preferences.weather_account_name,
    timezone: apiUser.preferences.timezone || 'UTC',
    locale: apiUser.preferences.locale || 'en_US',
    defaultBrand: apiUser.preferences.default_brand || 'pulsewindow'
  } : {
    unitsLength: 'metric',
    unitsWeight: 'metric',
    unitsTemperature: 'celsius',
    unitsSpeed: 'kph',
    dashboardColumns: '2',
    showWeatherWidget: false,
    timezone: 'UTC',
    locale: 'en_US',
    defaultBrand: 'pulsewindow'
  }

  // Map user data from snake_case to camelCase
  return {
    id: apiUser.id,
    email: apiUser.email || '',
    firstName: apiUser.first_name || '',
    lastName: apiUser.last_name || '',
    displayName: apiUser.display_name || `${apiUser.first_name || ''} ${apiUser.last_name || ''}`.trim() || apiUser.email || 'Unknown User',
    phoneNumber: apiUser.phone_number || undefined,
    contactPref: apiUser.contact_pref || 'email',
    avatarUrl: apiUser.avatar_url || undefined,
    isSuperUser: apiUser.is_super_user || false,
    isActive: apiUser.is_active ?? true,
    isBillingContact: apiUser.is_billing_contact || false,
    defaultAccountId: apiUser.default_account_id || undefined,
    defaultAccountName: apiUser.default_account_name || undefined,
    preferences,
    createdAt: apiUser.created_at || new Date().toISOString(),
    updatedAt: apiUser.updated_at || apiUser.created_at || new Date().toISOString(),
    lastLogin: apiUser.last_login || undefined
  }
}

/**
 * Maps user data from camelCase frontend format to snake_case API format
 */
export function mapUserToApi(user: Partial<User>): any {
  const apiUser: any = {}

  if (user.email !== undefined) apiUser.email = user.email
  if (user.firstName !== undefined) apiUser.first_name = user.firstName
  if (user.lastName !== undefined) apiUser.last_name = user.lastName
  if (user.phoneNumber !== undefined) apiUser.phone_number = user.phoneNumber
  if (user.contactPref !== undefined) apiUser.contact_pref = user.contactPref
  if (user.isActive !== undefined) apiUser.is_active = user.isActive
  if (user.isBillingContact !== undefined) apiUser.is_billing_contact = user.isBillingContact

  return apiUser
}
